'use client';

import {
  SELECTED_OPTIONS_KEY,
  SUBFLOW_PRODUCT_ANCHOR,
  addSubscriptionAddOns,
  getBirthDate,
  getBundleAsset,
  getPreviewItems,
  getSelectedOptions,
  extractItems,
  SCHEDULED_ITEM_SCHEDULED_TYPES,
  hasCoursePackItems,
  addCoursePackItems,
  finalizeCart,
  startCheckout,
  redirectToPlayGeniusCheckout,
  redirectToCart,
  QR_DATA_KEY,
} from '@/components/subflow-v3/components/utils';

import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';

import useAuth from '@/context/AuthContext';

import { CartItemProperty, ERROR_TYPES, ITEM_ORIGINS } from '@/core/constants';

import useFeature from '@/hooks/useFeature';
import { PaymentOption } from '@/hooks/usePaymentOptions';

import { cart } from '@/lib/cart';
import { ShopifyCartItem } from '@/lib/shopify';

import { isDateWithinMonthsFromNow } from '@/utils/dates/isDateWithinMonthsFromNow';
import { getAuthTokenClient, getLocaleInfo } from '@/utils/helpers';
import { noticeError } from '@/utils/newrelicHelpers';

import {
  convertCartLineItemPropertiesToAttributes,
  getCartLineItemProperty,
} from '@/utils/shopify';

import { trackAddToCart } from '@/utils/subflowAnalyticsHelpers';

import { TypeSubFlowV2Translations } from '@/utils/translations/TypeSubFlowV2Translations';

import { useStepTracking } from './subFlowAnalytics';

import { SubscriptionAddOn } from './useAddOns';
import useLocale from './useLocale';
import { useOrderPreview } from './useOrderPreview';
import { SubscriptionPlanSettings } from './usePlayGeniusCheckout/payloadBuilders/types';

export enum Referrers {
  Cart = 'cart',
  Subflow = 'subflow',
}

export type ContinueToCheckoutSettings = {
  selectedPaymentOptionOverride?: PaymentOption;
  subscriptionAddOnsOverride?: SubscriptionAddOn[];
  callback?: () => void;
  isSimplifiedSubflow?: boolean;
  shouldOmitDob?: boolean;
  shouldIgnoreOnFlowComplete?: boolean;
};

export const useStartCheckout = (
  t?: TypeSubFlowV2Translations,
  version = 'v2',
  referrer = Referrers.Subflow,
) => {
  const locale = useLocale();

  const localeInfo = getLocaleInfo(locale);

  const isRewriteCheckoutEnable =
    useFeature('growth__enable-checkout-rewrite') === true;

  const { data } = useSubFlowV3Data();

  const { data: previewData, isLoading: isPreviewDataLoading } =
    useOrderPreview(data?.childInfo || { day: '', month: '', year: '' });

  const { onFlowComplete } = useStepTracking(version);

  const { user } = useAuth();

  const userUuid = user?.attributes['custom:account_manager_id'];

  const continueToCheckout = async (settings?: ContinueToCheckoutSettings) => {
    if (typeof window === 'undefined' || !data) return;

    let cartData = await cart.get();

    const {
      selectedPaymentOptionOverride,
      subscriptionAddOnsOverride,
      callback,
      isSimplifiedSubflow,
      shouldOmitDob,
      shouldIgnoreOnFlowComplete,
    } = settings || {};

    const selectedPaymentOption =
      selectedPaymentOptionOverride || data.paymentOption;

    const isRecurringPuf = false;

    const subsAddOns = subscriptionAddOnsOverride || data.subscriptionAddOns;

    cartData.attributes.language = data.language;

    (window as any).cartCurrencyCode = cartData?.currency;

    const { items, previousPlays, subscriptionPlanItems } = extractItems(
      cartData.items,
      data,
    );

    const originalItems = [...cartData.items];

    cartData.items = items;

    const previewItems = await getPreviewItems(previewData, locale);

    const bundleAsset = getBundleAsset(previewItems);

    const selectedOptions = getSelectedOptions(data);

    const subscriptionsPayload = {
      child_name: data?.childInfo.name || t?.noName,
      child_uuid: data?.existingChildUuid,
      date_of_birth: getBirthDate(data?.childInfo),
      subscription_anchor: SUBFLOW_PRODUCT_ANCHOR,
      starting_kit_anchor: data?.startingKit?.anchor,
      tagline: selectedPaymentOption?.tagline,
      bundle: {
        badge_title: selectedPaymentOption?.badge_title,
        bundle_title: null,
        is_default: selectedPaymentOption?.is_default,
        kits: null,
        name: selectedPaymentOption?.bundle_title,
        price: selectedPaymentOption?.price,
        savings: selectedPaymentOption?.savings,
        shopify_variant_id: selectedPaymentOption?.shopify_variant_id,
        size: selectedPaymentOption?.size,
        uuid: selectedPaymentOption?.uuid,
      },
      bundleDiscountedPrice: selectedPaymentOption?.price,
      bundleAsset,
      bundleVariantId: selectedPaymentOption?.shopify_variant_id,
      properties: {
        item_origin: ITEM_ORIGINS.SUB_FLOW,
      },
      has_opted_in_to_recurring_puf: isRecurringPuf,
    };

    cartData.subscriptions = [subscriptionsPayload];

    if (subscriptionsPayload.bundle.uuid == undefined) {
      const errorPayload = JSON.stringify({ data, cartData });

      noticeError(ERROR_TYPES.SUBFLOW_V3_CHECKOUT_ERROR, errorPayload);
    }

    if (subsAddOns.length) {
      cartData = addSubscriptionAddOns(
        cartData,
        subsAddOns,
        data.paymentOption,
      );
    }

    if (selectedOptions) {
      cartData.subscriptions[0][SELECTED_OPTIONS_KEY] = selectedOptions;
    }

    if (hasCoursePackItems(cartData?.items)) {
      addCoursePackItems(cartData);
    }

    if (previousPlays?.length > 0) {
      cartData[SCHEDULED_ITEM_SCHEDULED_TYPES.PREVIOUS_PLAY] = previousPlays;
    }

    if (subscriptionPlanItems.length) {
      cartData['subscription_plans'] = subscriptionPlanItems
        ?.map((plan: ShopifyCartItem) => {
          const attributes = convertCartLineItemPropertiesToAttributes(
            plan.properties,
          );

          const subscriptionPlanSettings = getCartLineItemProperty(
            CartItemProperty.SubscriptionPlanSettings,
            attributes,
            { parseJson: true },
          );

          return subscriptionPlanSettings
            ? (subscriptionPlanSettings as unknown as SubscriptionPlanSettings)
            : null;
        })
        .filter((plan: ShopifyCartItem) => plan !== null);
    }

    if (typeof window !== 'undefined') {
      const storage = JSON.parse(
        window.localStorage.getItem(QR_DATA_KEY) || '{}',
      );

      if (storage?.lookerInTargetQR) {
        window.localStorage.removeItem(QR_DATA_KEY);

        const { childInfo } = data;

        if (
          isDateWithinMonthsFromNow({
            year: Number(childInfo.year),
            month: Number(childInfo.month),
            day: Number(childInfo.day),
            months: 3,
          })
        ) {
          cartData.subscriptions = [
            {
              ...subscriptionsPayload,
              starting_product_anchor: 'kit-the-charmer',
            },
          ];
        }
      }
    }

    // Logged in user uuid sent to start checkout
    if (userUuid) {
      cartData.user_uuid = userUuid;
    }

    await finalizeCart(cartData, data, subscriptionsPayload, originalItems);

    // We need to refetch the cart for the updated items
    const finalCartData = await cart.get();

    // Override product_type for BFCM Bundle
    if (isSimplifiedSubflow) {
      finalCartData.items = finalCartData.items.map((item: any) => {
        return {
          ...item,
          product_type: 'BFCM Bundle',
        };
      });
    }

    await trackAddToCart(finalCartData);

    const userJwt = await getAuthTokenClient();

    const startCheckoutCartData = JSON.parse(JSON.stringify(cartData));

    if (shouldOmitDob) {
      delete startCheckoutCartData.subscriptions[0].date_of_birth;
      delete startCheckoutCartData.subscriptions[0].starting_kit_anchor;
      delete startCheckoutCartData.subscriptions[0].verifiedDob;

      startCheckoutCartData.subscriptions[0].startingKitAnchor =
        data?.startingKit?.anchor;
    }

    const invoice: any = await startCheckout(
      startCheckoutCartData,
      { 'growth__enable-checkout-rewrite': isRewriteCheckoutEnable },
      userJwt,
    );

    if (callback) {
      callback();
    }

    if (invoice) {
      !shouldIgnoreOnFlowComplete && onFlowComplete();

      if (!isSimplifiedSubflow) {
        await redirectToPlayGeniusCheckout(
          invoice.uuid,
          isRecurringPuf,
          localeInfo,
          isRewriteCheckoutEnable,
          data.referralCode,
          referrer,
        );
      } else {
        await redirectToCart();
      }
    }
  };

  return {
    continueToCheckout,
    isCheckoutLoading: isPreviewDataLoading,
  };
};
